export const BUDGET_TABS = [
  // Labels renamed for clarity (Cost Reports = view/print/compare; Edit Budgets =
  // add/edit/delete). Paths kept stable so existing bookmarks/links don't break.
  { label: 'Cost Reports', path: '/budget/overview' },
  { label: 'Edit Budgets', path: '/budget/manage-budgets' },
];

/**
 * 🔀 0 READS "0 days" EVERYWHERE SINCE 2026-10-19 (owner, Pete: "ok", on
 * renaming every remaining user-visible "Off" that means allocation 0 —
 * after his earlier *"I dont think we should have an off option. change it to
 * 0 days"* on the default field). One label change, no behaviour change: the
 * value is still `0`, still a REAL allocation (never a falsy test). Every
 * reader inherits it — the crew table column, the Gantt bar label
 * (`getAllocationLabel`), the budget PDF (`utils/budgetManagePdf.js`) and the
 * default-allocation dropdowns. "Off hire" / "Off-Hired" are hires and are
 * not this word.
 */
export const ALLOCATION_OPTIONS = [
  { value: 0, label: '0 days' },
  { value: 0.2, label: '1 day' },
  { value: 0.4, label: '2 days' },
  { value: 0.6, label: '3 days' },
  { value: 0.8, label: '4 days' },
  { value: 1.0, label: 'Full week' },
];

/**
 * The member's DEFAULT ALLOCATION dropdown (edit panel, level 2, add-member
 * form) and its read-only twin. Owner, Pete, 2026-10-19: *"I dont think we
 * should have an off option. change it to 0 days"* — the default is a day
 * count for weeks a date change ADDS, so 0 is "0 days" (a drag lays only the
 * dragged-to day). 🔀 Since 2026-10-19 `ALLOCATION_OPTIONS` itself reads
 * "0 days" (see above), so this is the SAME list — kept as a name so its
 * readers need no edit, never a second copy of the values.
 */
export const DEFAULT_ALLOCATION_OPTIONS = ALLOCATION_OPTIONS;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WEEK_SHAPE_PRESETS — the per-week control on the crew day grid (Task 6 of
 * the 6th/7th-day premium wage feature).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🔑 THIS DOES NOT REPLACE `ALLOCATION_OPTIONS`, IT SITS ON TOP OF IT.
 * `ALLOCATION_OPTIONS` is still the control on the ADD-MEMBER form (which
 * sets `default_allocation`, a fraction column) and still supplies the
 * collapsed row's "Allocation" label. Only the PER-WEEK `<select>` inside the
 * expanded member panel moved to these presets.
 *
 * ─── THE MAPPING, AND WHY IT IS NOT `dayCount / 5` ────────────────────────
 * `crew_week_overrides.allocation` still drives COMPUTER RENTAL and all three
 * ALLOWANCES (`computeCrewWeeks`); only LABOUR is on the day basis
 * (`server/helpers/crewDays.js`). So the preset writes BOTH: the day set, and
 * `Math.min(days, 5) / 5` as the allocation.
 *
 *   0..5 days -> 0 / 0.2 / 0.4 / 0.6 / 0.8 / 1.0 — byte-identical to the six
 *                values `ALLOCATION_OPTIONS` has always offered, so every
 *                shape reachable before this change costs exactly what it did.
 *   6..7 days -> 1.0, CLAMPED. A laptop does not cost more because someone
 *                worked a Saturday, and neither does a per-week allowance.
 *                The extra labour rides on the DAY count, which is the whole
 *                point of the day model.
 *
 * 🔴 THE ZERO-CASE SEAM — read before decoupling these two writes.
 * "Off" is `dayCount === 0`, which writes allocation 0 AND an empty day set
 * for that week, and that is why unticking every day of a week costs nothing
 * TODAY: the fraction path already honours an explicit override of 0.
 * **Do not decouple the two writes** — i.e. do not let the grid clear a
 * week's days while leaving its allocation alone. A member whose day rows are
 * ALL deleted is byte-identical to a member who has no day data yet, and
 * `resolveWorkedDays` correctly charges that member the full fraction-path
 * cost. That is ledger entry **S2-F** in
 * `.superpowers/sdd/2026-08-20-sixth-seventh-day-wages/progress.md`, which is
 * OPEN: the deliberate-zero marker (a per-member flag or an explicit zero
 * row) is being decided separately and does not exist yet. When it lands,
 * this is where a `deliberatelyZero` preset drops in — the presets, the
 * allocation mapping and the grid's tick handler are already the only three
 * places that decide what "no days" means.
 * 🪤 Do NOT solve it by treating an empty day set as zero: that silently
 * drops every cloned version and every un-backfilled member to zero labour.
 */
export const WEEK_SHAPE_PRESETS = [
  { value: 0, label: '0 days' },   // was "Off" — relabelled 2026-10-19, see ALLOCATION_OPTIONS
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 4, label: '4 days' },
  { value: 5, label: '5 days' },
  { value: 6, label: '6-day week' },
  { value: 7, label: '7-day week' },
];

/**
 * The allocation a given worked-day count implies. See the block above — the
 * clamp at 5 is the money-relevant half.
 */
export function allocationForDayCount(dayCount) {
  const n = Math.max(0, Math.min(7, Math.round(Number(dayCount) || 0)));
  return Math.min(n, 5) / 5;
}

/**
 * The day count a legacy allocation fraction implies, Monday-first.
 * `Math.round(alloc * 5)` is the SAME expression Task 3's boot backfill uses
 * (`server/helpers/crewDayBackfill.js`), deliberately — a grid that seeded
 * itself differently from the backfill would show a member days they are not
 * being charged for.
 */
export function dayCountForAllocation(allocation) {
  const a = Number(allocation);
  if (!Number.isFinite(a)) return 5;
  return Math.max(0, Math.min(7, Math.round(a * 5)));
}

export const BUDGET_HEALTH_THRESHOLDS = {
  good: 70,     // < 70% spent = green
  warning: 90,  // 70-90% = amber
  // > 90% = red/critical
};

export const BUDGET_HEALTH_COLORS = {
  good: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-300',
  },
  warning: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-800 dark:text-amber-300',
  },
  critical: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
  },
};

export function getBudgetHealth(percentSpent) {
  if (percentSpent < BUDGET_HEALTH_THRESHOLDS.good) return 'good';
  if (percentSpent < BUDGET_HEALTH_THRESHOLDS.warning) return 'warning';
  return 'critical';
}

export const GRADE_COLORS = [
  { bg: 'bg-blue-500', text: 'text-white', hex: '#3B82F6' },
  { bg: 'bg-green-500', text: 'text-white', hex: '#22C55E' },
  { bg: 'bg-amber-500', text: 'text-white', hex: '#F59E0B' },
  { bg: 'bg-purple-500', text: 'text-white', hex: '#A855F7' },
  { bg: 'bg-rose-500', text: 'text-white', hex: '#F43F5E' },
  { bg: 'bg-teal-500', text: 'text-white', hex: '#14B8A6' },
  { bg: 'bg-indigo-500', text: 'text-white', hex: '#6366F1' },
  { bg: 'bg-orange-500', text: 'text-white', hex: '#F97316' },
];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE BUILT-IN COMPUTER ALLOWANCE — ITS NAME, AND HOW IT IS LABELLED
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🔑 COMPUTER RENTAL IS ONE TYPE OF ALLOWANCE, NOT A CATEGORY BESIDE THEM.
 * Owner, stated three times, the last in capitals: *"remember computer is one
 * type of allowance and not different. Its just a common allowance so that's
 * why its included as a default example."* It is offered as a built-in slot
 * because every production has one — not because it is a different KIND of
 * thing. The member form draws it as allowance row #0 with its name locked;
 * everything else must read the same way.
 *
 * 🪤 A RATE IS NOT A COST, AND THE RULING DOES NOT MERGE THOSE. The ruling
 * says computer is not a special kind of allowance; it does not say a per-week
 * RATE (`crew_grades.computer_rental`, `crew_members.computer_rental` — an
 * INPUT) is the same fact as an engagement COST (`computer_cost` — a RESULT).
 * A column showing a per-week rate stays a per-week rate. What changes is
 * whether it is presented as a separate CATEGORY.
 *
 * ─── WHY TWO CONSTANTS AND NOT ONE ───────────────────────────────────────
 * `COMPUTER_ALLOWANCE_NAME` is the allowance's NAME — the string a user reads
 * where the other three slots show the name they typed (the locked name box,
 * the compare drawer's slot row label, the cost-summary breakdown). It is
 * "Computer Rental" because that is what the thing is called.
 *
 * `COMPUTER_ALLOWANCE_LABEL` is what labels a per-week AMOUNT control or
 * column for that allowance, where the peers read "<name> Allowance". It says
 * the word "Allowance" out loud, which is the whole point: a heading reading
 * "Computer Rental" among "Weekly Rate" and "6th Day" reads as another cost
 * category, and that is the reading being removed.
 *
 * 🪤 CALL SITES APPEND THEIR OWN UNIT SUFFIX — `(£/wk)` on screen, `/wk` in a
 * tight card, none in a PDF header — exactly as "Weekly Rate" already does.
 * Do NOT bake a unit in here; there is no single right one.
 */
export const COMPUTER_ALLOWANCE_NAME = 'Computer Rental';
export const COMPUTER_ALLOWANCE_LABEL = 'Computer Allowance';
